import { AxiosError } from 'axios';
import { put, call, select, takeLatest } from 'redux-saga/effects';
import { selectors as clientSelectors } from 'store/clients';
import {
  types,
  Admin,
  AdminWithActivity,
  GetAdminRequest,
  CreateAdminRequest,
  UpdateAdminRequest,
  DeleteAdminRequest
} from './types';
import { API } from 'services/api/base';
import { getErrorCode, getErrorMessage } from 'services/api/normalizer';

export function* getAdminsAsync() {
  yield put({ type: types.GET_ADMINS.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const adminsApi = new API(`clients/${clientId}/admins`);
    const response: AdminWithActivity[] = yield call(adminsApi.getAll);

    yield put({ type: types.GET_ADMINS.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_ADMINS.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_ADMINS.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.GET_ADMINS.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.GET_ADMINS.PENDING, payload: false });
  }
}

export function* getAdminAsync({ payload }: GetAdminRequest) {
  const { id } = payload;
  yield put({ type: types.GET_ADMIN.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const adminsApi = new API(`clients/${clientId}/admins`);
    const response: Admin[] = yield call(adminsApi.getOne, id);

    yield put({ type: types.GET_ADMIN.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_ADMIN.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_ADMIN.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.GET_ADMIN.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.GET_ADMIN.PENDING, payload: false });
  }
}

export function* createAdminAsync({ payload }: CreateAdminRequest) {
  yield put({ type: types.CREATE_ADMIN.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const adminsApi = new API(`clients/${clientId}/admins`);
    yield call(adminsApi.create, payload);

    yield put({ type: types.CREATE_ADMIN.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;
    const errMsg = getErrorMessage(err as AxiosError) as string;

    if (errorCode === 409) {
      yield put({ type: types.CREATE_ADMIN.ERROR, payload: errMsg || `${payload.email} has already been registered.` });
    } else if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CREATE_ADMIN.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CREATE_ADMIN.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.CREATE_ADMIN.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.CREATE_ADMIN.PENDING, payload: false });
  }
}

export function* updateAdminAsync({ payload }: UpdateAdminRequest) {
  yield put({ type: types.UPDATE_ADMIN.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const adminsApi = new API(`clients/${clientId}/admins`);
    yield call(adminsApi.update, payload);

    yield put({ type: types.UPDATE_ADMIN.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;
    const errMsg = getErrorMessage(err as AxiosError) as string;

    if (errorCode === 409) {
      yield put({ type: types.UPDATE_ADMIN.ERROR, payload: errMsg || `${payload.email} has already been registered.` });
    } else if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_ADMIN.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_ADMIN.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.UPDATE_ADMIN.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.UPDATE_ADMIN.PENDING, payload: false });
  }
}

export function* deleteAdminAsync({ payload }: DeleteAdminRequest) {
  const { id } = payload;
  yield put({ type: types.DELETE_ADMIN.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const adminsApi = new API(`clients/${clientId}/admins`);
    yield call(adminsApi.destroy, id);

    yield put({ type: types.DELETE_ADMIN.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.DELETE_ADMIN.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.DELETE_ADMIN.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.DELETE_ADMIN.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.DELETE_ADMIN.PENDING, payload: false });
  }
}

export function* getValidPinCodeAsync() {
  yield put({ type: types.GET_PINCODE.PENDING, payload: true });

  try {
    const api = new API(`clients/pinCode`);
    const { pinCode } = yield call(api.getAll);

    yield put({ type: types.GET_PINCODE.SUCCESS, payload: pinCode });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_PINCODE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_PINCODE.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.GET_PINCODE.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.GET_PINCODE.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_ADMINS.REQUEST, getAdminsAsync),
  takeLatest(types.GET_ADMIN.REQUEST, getAdminAsync),
  takeLatest(types.CREATE_ADMIN.REQUEST, createAdminAsync),
  takeLatest(types.UPDATE_ADMIN.REQUEST, updateAdminAsync),
  takeLatest(types.DELETE_ADMIN.REQUEST, deleteAdminAsync),
  takeLatest(types.GET_PINCODE.REQUEST, getValidPinCodeAsync)
];
